import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { formatDZ } from '../../../utils/currency';

const ProductManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');

  const products = [
    {
      id: 'PRD-001',
      name: 'iPhone 15 Pro Max',
      category: 'Phones',
      price: 1199.99,
      stock: 45,
      status: 'active',
      image: 'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400',
      sku: 'IPH15PM-256-TB',
      lastUpdated: '2024-09-23'
    },
    {
      id: 'PRD-002',
      name: 'MacBook Pro 16"',
      category: 'Laptops',
      price: 2499.99,
      stock: 12,
      status: 'active',
      image: 'https://images.unsplash.com/photo-1541807084-5c52b6b3adef?w=400',
      sku: 'MBP16-M3-1TB',
      lastUpdated: '2024-09-22'
    },
    {
      id: 'PRD-003',
      name: 'AirPods Pro 2nd Gen',
      category: 'AirPods',
      price: 249.99,
      stock: 0,
      status: 'out_of_stock',
      image: 'https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=400',
      sku: 'APP2-USB-C',
      lastUpdated: '2024-09-21'
    },
    {
      id: 'PRD-004',
      name: 'USB-C Fast Charger 65W',
      category: 'Chargers',
      price: 59.99,
      stock: 156,
      status: 'active',
      image: 'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400',
      sku: 'CHG-USBC-65W',
      lastUpdated: '2024-09-23'
    },
    {
      id: 'PRD-005',
      name: 'Wireless Mouse Pro',
      category: 'Accessories',
      price: 89.99,
      stock: 3,
      status: 'low_stock',
      image: 'https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=400',
      sku: 'MSE-WL-PRO',
      lastUpdated: '2024-09-20'
    },
    {
      id: 'PRD-006',
      name: 'Gaming Headset RGB',
      category: 'Accessories',
      price: 129.99,
      stock: 28,
      status: 'draft',
      image: 'https://images.unsplash.com/photo-1599669454699-248893623440?w=400',
      sku: 'HDS-RGB-GM',
      lastUpdated: '2024-09-19'
    }
  ];

  const getStockStatus = (stock, status) => {
    if (status === 'out_of_stock' || stock === 0) return { label: 'Out of Stock', color: 'error' };
    if (status === 'low_stock' || stock < 10) return { label: 'Low Stock', color: 'warning' };
    return { label: 'In Stock', color: 'success' };
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-success/20 text-success border-success/30',
      draft: 'bg-warning/20 text-warning border-warning/30',
      out_of_stock: 'bg-error/20 text-error border-error/30',
      low_stock: 'bg-warning/20 text-warning border-warning/30'
    };
    return colors?.[status] || colors?.active;
  };

  const filteredProducts = products?.filter(product => {
    const matchesSearch = product?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         product?.sku?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product?.category === categoryFilter;
    const matchesStock = stockFilter === 'all' || 
                        (stockFilter === 'in_stock' && product?.stock > 10) ||
                        (stockFilter === 'low_stock' && product?.stock > 0 && product?.stock <= 10) ||
                        (stockFilter === 'out_of_stock' && product?.stock === 0);
    
    return matchesSearch && matchesCategory && matchesStock;
  });

  return (
    <div className="holographic-card rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground">Product Management</h3>
          <Button variant="default" iconName="Plus" iconPosition="left">
            Add Product
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search products by name or SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>
          
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e?.target?.value)}
            className="px-3 py-2 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Categories</option>
            <option value="Phones">Phones</option>
            <option value="Laptops">Laptops</option>
            <option value="AirPods">AirPods</option>
            <option value="Chargers">Chargers</option>
            <option value="Accessories">Accessories</option>
          </select>

          <select
            value={stockFilter}
            onChange={(e) => setStockFilter(e?.target?.value)}
            className="px-3 py-2 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Stock Levels</option>
            <option value="in_stock">In Stock</option>
            <option value="low_stock">Low Stock</option>
            <option value="out_of_stock">Out of Stock</option>
          </select>

          <Button variant="outline" iconName="Filter">
            More Filters
          </Button>
        </div>
      </div>
      {/* Product Grid */}
      <div className="p-6">
        {filteredProducts?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredProducts?.map((product) => {
              const stockStatus = getStockStatus(product?.stock, product?.status);
              
              return (
                <div key={product?.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-lg transition-all duration-200">
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted/20 flex-shrink-0">
                      <Image
                        src={product?.image}
                        alt={product?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-foreground text-sm truncate">{product?.name}</h4>
                        <div className="flex items-center space-x-1 ml-2">
                          <Button variant="ghost" size="sm" iconName="Edit" />
                          <Button variant="ghost" size="sm" iconName="MoreVertical" />
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mb-1">SKU: {product?.sku}</p>
                      <p className="text-xs text-muted-foreground mb-2">{product?.category}</p>
                      
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-bold text-foreground">{formatDZ(product?.price)}</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(stockStatus?.color)}`}>
                          {stockStatus?.label}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          Stock: <span className="font-medium text-foreground">{product?.stock}</span>
                        </span>
                        <span className="text-muted-foreground">
                          Updated: {product?.lastUpdated}
                        </span>
                      </div>
                      
                      {/* Stock Level Bar */}
                      <div className="mt-2">
                        <div className="w-full bg-muted/30 rounded-full h-1">
                          <div 
                            className={`h-1 rounded-full transition-all duration-300 ${
                              product?.stock === 0 ? 'bg-error' :
                              product?.stock < 10 ? 'bg-warning' : 'bg-success'
                            }`}
                            style={{ width: `${Math.min((product?.stock / 100) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No products found</h3>
            <p className="text-muted-foreground mb-4">No products match your current filter criteria.</p>
            <Button variant="outline" onClick={() => {
              setSearchTerm('');
              setCategoryFilter('all');
              setStockFilter('all');
            }}>
              Clear Filters
            </Button>
          </div>
        )}
      </div>
      {/* Bulk Actions */}
      {filteredProducts?.length > 0 && (
        <div className="p-6 border-t border-border bg-muted/10">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Showing {filteredProducts?.length} of {products?.length} products
            </span>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Download">
                Export CSV
              </Button>
              <Button variant="outline" size="sm" iconName="Upload">
                Bulk Import
              </Button>
              <Button variant="outline" size="sm" iconName="RefreshCw">
                Sync Inventory
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;